import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPersonaChipClass, getRefacturacionStatusChip } from '../../../../utils/styles.js';
import { useAuth } from '../../../hooks/useAuth'; 

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';
import ModalDetalleRefacturacion from '../../../ui/ModalDetalleRefacturacion';

// FILTROS Y CONTROLADOR
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { useRefacturacionesTable } from './useRefacturacionesTable';

// --- HELPERS VISUALES ---
const parseToNumeric = (value) => {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    const clean = String(value).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
    return parseFloat(clean) || 0;
};

const formatCurrency = (value, moneda = '$') => {
    const numeric = parseToNumeric(value);
    return new Intl.NumberFormat('es-AR', { 
        style: 'currency', 
        currency: moneda === 'USD' ? 'USD' : 'ARS', 
        minimumFractionDigits: 0 
    }).format(numeric);
};

const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    if (dateString.seconds) return new Date(dateString.seconds * 1000).toLocaleDateString('es-AR');
    if (typeof dateString === 'string' && dateString.includes('-')) return dateString.split('-').reverse().join('/');
    return String(dateString);
};

const getImportePriority = (monto, moneda = '$') => {
    const val = parseToNumeric(monto);
    if (moneda === 'USD') return { label: 'P1', class: 'bg-red-200 text-red-900 border-red-200', val: 1 };
    if (val > 300000) return { label: 'P1', class: 'bg-red-200 text-red-900 border-red-200', val: 1 };
    if (val >= 100000) return { label: 'P2', class: 'bg-orange-200 text-orange-900 border-orange-200', val: 2 };
    return { label: 'P3', class: 'bg-yellow-100 text-yellow-900 border-yellow-200', val: 3 };
};

// --- FILA MEMOIZADA ---
const TableRow = memo(({ d, isSelected, isLocked, isLockedByMe, columnVisibility, onSelect, onAction, onUnlock }) => {
    const priority = getImportePriority(d.primaEmitida, d.moneda);

    return (
        <tr 
            onClick={() => !isLocked && onSelect(d.id)} 
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''}
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.displayNombre && (
                <td className="p-2.5 px-4 border-b border-r border-slate-100 text-sm font-bold text-slate-900 truncate uppercase max-w-[300px]">
                    <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{d.displayNombre}</span>
                        {d.tieneDeudaMultiple && (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded-md shrink-0 shadow-sm" title="Otras pólizas con deuda">
                                <Icons.MultiDebt />
                                <span className="text-[10px] font-black text-amber-700">+{d.packCount}</span>
                            </div>
                        )}
                    </div>
                </td>
            )}
            
            {columnVisibility.displayDoc && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-sm font-mono text-slate-500 w-32">{d.displayDoc}</td>}
            {columnVisibility.nPoliza && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-sm font-bold text-slate-900 w-32">{d.nPoliza}</td>}
            
            {columnVisibility.numeroEndoso && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center w-28">
                    <div className="flex flex-col items-center">
                        <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 uppercase">E-{d.numeroEndoso || '0'}</span>
                        {d.numeroFactura && <span className="text-[9px] text-slate-400 font-bold mt-1">F: {d.numeroFactura}</span>}
                    </div>
                </td>
            )}

            {columnVisibility.compania && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-left w-40">
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg w-fit group-hover:bg-white transition-colors">
                        <div className="text-blue-400"><Icons.Company /></div>
                        <span className="text-[11px] text-blue-800 font-bold uppercase truncate max-w-[140px]">{d.compania}</span>
                    </div>
                </td>
            )}

            {columnVisibility.riesgo && (
                <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                    <div className="flex flex-col">
                        <span className="font-bold text-slate-800 tracking-tight">{d.riesgo}</span>
                        <span className="text-[11px] font-medium text-slate-500 uppercase">{d.subriesgo || 'General'}</span>
                    </div>
                </td>
            )}

            {columnVisibility.finVigencia && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-red-700 tabular-nums bg-red-50/20">{formatDateShort(d.finVigencia)}</td>}

            {columnVisibility.importeDeuda && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center w-40">
                    <div className="flex flex-col items-center justify-center gap-1">
                        <span className={`px-3 py-1 rounded-lg text-sm font-black border tabular-nums ${priority.class}`}>
                            {formatCurrency(d.primaEmitida, d.moneda)}
                        </span>
                        {d.isUrgentRenovacion && (
                            <div className={`text-[8px] font-black px-1.5 rounded animate-pulse uppercase tracking-tighter ${d.isExpired ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                                {d.isExpired ? 'Vencido' : 'Próximo Vto'}
                            </div>
                        )}
                    </div>
                </td>
            )}

            {columnVisibility.formaPago && (
                <td className="p-2.5 px-3 text-center border-b border-r border-slate-100 w-32">
                    <span className="bg-slate-50 text-slate-600 border border-slate-200 rounded-lg uppercase px-3 py-1 text-[10px] font-bold">{d.formaPago || '-'}</span>
                </td>
            )}

            {columnVisibility.estadoRefacturacion && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-[10px] w-44">
                    <span className={`px-3 py-1 rounded-full font-black uppercase border ${getRefacturacionStatusChip(d.estadoRefacturacion)} whitespace-nowrap`}>
                        {d.estadoRefacturacion}
                    </span>
                </td>
            )}

            {columnVisibility.fechaReclamo && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-800 tabular-nums w-32">{formatDateShort(d.fechaReclamo)}</td>}

            {columnVisibility.quienGestiono && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center w-40">
                    {d.quienGestiono ? (
                        <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(d.quienGestiono)}`}>
                            <span className="text-[10px] font-bold uppercase">{d.quienGestiono}</span>
                        </div>
                    ) : '-'}
                </td>
            )}

            {columnVisibility.canal && (
                <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                    <div className="flex flex-col">
                        <span className="font-bold text-slate-800 tracking-tight">{d.canal}</span>
                        <span className="text-[11px] font-medium text-slate-500 uppercase">{d.canalNombreDisplay}</span>
                    </div>
                </td>
            )}

            {/* Acciones */}
            <td className="p-2.5 px-2 text-center sticky right-0 z-[70] border-l border-b border-r border-slate-100 bg-white group-hover:bg-slate-50 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] w-24">
                <div className="flex justify-center items-center gap-1.5">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(d, 'view_ref_history'); }} className="p-1 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Historial"><Icons.History /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(d, 'view_policy_detail'); }} className="p-1 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all" title="Póliza"><Icons.Policy /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(d, 'view_client_ficha'); }} className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Cliente"><Icons.User /></button>
                    
                    {isLocked ? (
                        <div className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-100 animate-pulse">
                            <Icons.Lock /> <span className="text-[8px] font-black uppercase tracking-tighter">{d.bloqueo?.userName || 'Ocupado'}</span>
                        </div>
                    ) : (
                        isLockedByMe && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); onUnlock(d.path); }} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                            </button>
                        )
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const GestionRefacturaciones = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Consumimos nuestro cerebro estructurado de Refacturaciones
    const {
        loading,
        clientsCache,
        activeItem,
        activePolizaData,
        showPolicyModal,
        showFichaModal,
        showHistoryModal,
        tableState,
        itemsTable,
        paginatedData,
        activeItemPathRef,
        setShowPolicyModal,
        setShowFichaModal,
        setShowHistoryModal,
        setActiveItem,
        setActivePolizaData, // ◄ CORRECCIÓN: Importación del set de póliza destructurado con éxito
        handleAction,
        unlockPoliza
    } = useRefacturacionesTable();

    // Desestructuramos el estado síncrono del tableState
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        checkIsLocked: checkIsItemLocked
    } = tableState;

    const columnLabels = {
        displayNombre: "Cliente", displayDoc: "DNI/CUIT", nPoliza: "Póliza", numeroEndoso: "Endoso",
        compania: "Compañía", riesgo: "Riesgo/Sub", finVigencia: "Vencimiento", importeDeuda: "Importe",
        formaPago: "Forma Pago", estadoRefacturacion: "Estado", fechaReclamo: "Reclamo",
        quienGestiono: "Responsable", canal: "Canal"
    };

    // Liberación por descarga involuntaria de pestaña
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const itemsForCanalNombre = filterValues.canal?.length > 0 ? itemsTable.filter(d => filterValues.canal.includes(d.canal)) : itemsTable;
        const itemsForSubcanal = filterValues.canalNombreDisplay?.length > 0 ? itemsForCanalNombre.filter(d => filterValues.canalNombreDisplay.includes(d.canalNombreDisplay)) : itemsForCanalNombre;

        return [
            { name: 'search', label: 'Buscador', type: 'text', placeholder: 'Póliza, DNI, Cliente...', className: 'w-72' },
            { name: 'compania', label: 'Cía', type: 'select', isMulti: true, options: getUnique('compania') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: getUnique('subriesgo') },
            { name: 'formaPago', label: 'Forma Pago', type: 'select', isMulti: true, options: getUnique('formaPago') },
            { name: 'estadoRefacturacion', label: 'Estado', type: 'select', isMulti: true, options: getUnique('estadoRefacturacion') },
            { name: 'prioridadVal', label: 'Prioridad', type: 'select', isMulti: true, options: [{label: 'P1 (Crítica)', value: 1}, {label: 'P2 (Media)', value: 2}, {label: 'P3 (Baja)', value: 3}] },
            { name: 'quienGestiono', label: 'Responsable', type: 'select', isMulti: true, options: getUnique('quienGestiono') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombreDisplay', label: 'Nombre Canal', type: 'select', isMulti: true, options: [...new Set(itemsForCanalNombre.map(d => d.canalNombreDisplay))].sort().map(v => ({label: v, value: v})) },
            { name: 'subcanal', label: 'Subcanal / Asociado', type: 'select', isMulti: true, options: [...new Set(itemsForSubcanal.map(d => d.subcanal))].sort().map(v => ({label: v, value: v})) },
            { name: 'finVigencia', label: 'Vencimiento', type: 'date-range' },
            { name: 'fechaReclamo', label: 'Reclamo', type: 'date-range' },
        ];
    }, [itemsTable, filterValues]);

    if (loading) return (
        <div className="flex h-screen flex-col items-center justify-center bg-white space-y-4">
            <div className="w-12 h-12 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Sincronizando Deudas</p>
        </div>
    );

    return (
        <div className="flex h-screen w-full flex-col overflow-hidden bg-white font-['Inter'] antialiased text-left text-slate-900">
            <div className="h-1 w-full bg-indigo-600 shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button type="button" onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Gestión de Refacturaciones</h2>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Pendientes</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="refacturaciones-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white relative">
                <TableContainer minWidth="3200px">
                    <thead>
                        <tr className="bg-slate-900 text-white">
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[300px]" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Endoso" sortKey="numeroEndoso" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40" />
                            <SortableHeader label="Riesgo / Sub" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[220px]" />
                            <SortableHeader label="Fin Vigencia" sortKey="finVigencia" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Importe" sortKey="importeDeuda" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Forma Pago" sortKey="formaPago" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Estado" sortKey="estadoRefacturacion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Reclamo" sortKey="fechaReclamo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Responsable" sortKey="quienGestiono" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Canal / Nombre" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48" />
                            <th className="w-24 p-4 sticky right-0 top-0 bg-slate-900 border-b border-slate-700 z-[85] uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedData.map(item => {
                            const isLocked = checkIsItemLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <TableRow 
                                    key={item.id} 
                                    d={item} 
                                    isSelected={selectedRowId === item.id}
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    columnVisibility={columnVisibility}
                                    onSelect={setSelectedRowId}
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza}
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN UNIVERSAL */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="gestiones"
                    accentClass="text-indigo-900 bg-indigo-50 border-indigo-100"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            {activeItem && (
                <>
                    <ModalFichaCliente 
                        show={showFichaModal} 
                        onClose={() => { setShowFichaModal(false); setActiveItem(null); }} 
                        client={clientsCache[activeItem.clienteId] || { id: activeItem.clienteId }} 
                    />
                    <ModalDetallePoliza 
                        show={showPolicyModal} 
                        onClose={() => { setShowPolicyModal(false); setActivePolizaData(null); setActiveItem(null); }} 
                        poliza={activePolizaData} 
                    />
                    <ModalDetalleRefacturacion 
                        show={showHistoryModal} 
                        onClose={() => { if (activeItem) unlockPoliza(activeItem.path); setShowHistoryModal(false); setActiveItem(null); setActivePolizaData(null); }} 
                        poliza={activePolizaData} 
                        client={{ id: activeItem.clienteId, ...(clientsCache[activeItem.clienteId] || {}), nombre: activeItem.displayNombre }} 
                    />
                </>
            )}
        </div>
    );
};

export default GestionRefacturaciones;